import { useEffect } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ControlledField, FormDialog, ToggleButtonGroup } from '@shared/components';

import { AI_RUN_FEEDBACK_COMMENT_MAX_CHARS, AI_RUN_FEEDBACK_REASON_OPTIONS } from '../constants';
import { EAiRunFeedbackReason, EAiRunFeedbackSentiment, type TAiRunFeedbackInput } from '../types';

const useStyles = makeStyles({
  textarea: {
    minHeight: '120px',
    '& textarea': {
      minHeight: '120px',
    },
  },
});

const schema = yup
  .object({
    reasons: yup.array(yup.mixed<EAiRunFeedbackReason>().defined()).defined().default([]),
    comment: yup
      .string()
      .trim()
      .max(AI_RUN_FEEDBACK_COMMENT_MAX_CHARS, `Comment cannot exceed ${AI_RUN_FEEDBACK_COMMENT_MAX_CHARS} characters.`)
      .default(''),
  })
  .required();

type TFormData = yup.InferType<typeof schema>;

const DEFAULT_FORM_DATA: TFormData = {
  reasons: [],
  comment: '',
};

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  submittedFeedback: TAiRunFeedbackInput | null;
  isSubmitting: boolean;
  onSubmit: (feedback: TAiRunFeedbackInput) => void;
};

const AiRunFeedbackNegativeDialog = ({ isOpen, setIsOpen, submittedFeedback, isSubmitting, onSubmit }: TProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: DEFAULT_FORM_DATA,
  });

  useEffect(() => {
    if (!isOpen) return;
    reset(
      submittedFeedback?.sentiment === EAiRunFeedbackSentiment.NEGATIVE
        ? {
            reasons: submittedFeedback.reasons ?? DEFAULT_FORM_DATA.reasons,
            comment: submittedFeedback.comment ?? DEFAULT_FORM_DATA.comment,
          }
        : DEFAULT_FORM_DATA
    );
  }, [isOpen, submittedFeedback]);

  const submitNegativeFeedback = (data: TFormData) => {
    const feedback: TAiRunFeedbackInput = { sentiment: EAiRunFeedbackSentiment.NEGATIVE };
    if (data.reasons.length > 0) feedback.reasons = data.reasons;
    if (data.comment) feedback.comment = data.comment;

    onSubmit(feedback);
  };

  const classes = useStyles();

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Send negative feedback"
      onSubmit={handleSubmit(submitNegativeFeedback)}
      submitLabel="Send"
      isSubmitting={isSubmitting}
    >
      <div className="flex flex-col gap-4">
        <Controller
          name="reasons"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              options={AI_RUN_FEEDBACK_REASON_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              disabled={isSubmitting}
            />
          )}
        />
        <ControlledField
          inputType="textarea"
          name="comment"
          control={control}
          errors={errors}
          label="Tell us more (optional)"
          placeholder="Share details to help us improve"
          resize="vertical"
          readOnly={isSubmitting}
          className={classes.textarea}
        />
      </div>
    </FormDialog>
  );
};

export default AiRunFeedbackNegativeDialog;
